import { apiClient } from '@features/shared/api/client'
import type {
  AutomationCreateRequest,
  AutomationDefinitionContract,
  AutomationDeliveryResponse,
  AutomationDetailResponse,
  AutomationDraftCreateRequest,
  AutomationEmailDeliveryResponse,
  AutomationFileLimitsResponse,
  AutomationFileLinkResponse,
  AutomationFileResponse,
  AutomationScheduleCalendarResponse,
  AutomationPageParams,
  AutomationPreviewFileRequest,
  AutomationPreviewRequest,
  AutomationPreviewResponse,
  AutomationRunListParams,
  AutomationRunPageResponse,
  AutomationRunResponse,
  AutomationRestoreRequest,
  AutomationUpdateRequest,
  AutomationVersionResponse,
  EmailPurposeCheckRequest,
  EmailPurposeCheckResponse,
  StartAutomationRunRequest,
  StartAutomationRunResponse,
} from './contracts'

const path = (automationId: string) => `/automations/${encodeURIComponent(automationId)}`

/**
 * Publishing, creating or testing an email sent from an Outlook mailbox
 * checks the sender in Exchange first, which can take most of a minute.
 */
const SENDER_CHECK_TIMEOUT_MS = 90000

/** GET /automations, ordered by name. */
export async function listAutomations(): Promise<AutomationDetailResponse[]> {
  const { data } = await apiClient.get<AutomationDetailResponse[]>('/automations')
  return data
}

export async function getAutomation(automationId: string): Promise<AutomationDetailResponse> {
  const { data } = await apiClient.get<AutomationDetailResponse>(path(automationId))
  return data
}

export async function createAutomation(
  payload: AutomationCreateRequest
): Promise<AutomationDetailResponse> {
  const { data } = await apiClient.post<AutomationDetailResponse>('/automations', payload, {
    timeout: SENDER_CHECK_TIMEOUT_MS,
  })
  return data
}

/**
 * The editor's first save: a new automation with only a draft, in any state.
 * Nothing is published until someone publishes it.
 */
export async function createAutomationAsDraft(
  payload: AutomationDraftCreateRequest
): Promise<AutomationDetailResponse> {
  const { data } = await apiClient.post<AutomationDetailResponse>('/automations', payload, {
    params: { draft: true },
  })
  return data
}

export async function updateAutomation(
  automationId: string,
  payload: AutomationUpdateRequest
): Promise<AutomationDetailResponse> {
  const { data } = await apiClient.patch<AutomationDetailResponse>(path(automationId), payload)
  return data
}

/** The drafts first, then every other version, newest first (`state` keeps only those). */
export async function listAutomationVersions(
  automationId: string,
  params: AutomationPageParams = {}
): Promise<AutomationVersionResponse[]> {
  const { data } = await apiClient.get<AutomationVersionResponse[]>(
    `${path(automationId)}/versions`,
    { params }
  )
  return data
}

export async function getAutomationVersion(
  automationId: string,
  versionId: string
): Promise<AutomationVersionResponse> {
  const { data } = await apiClient.get<AutomationVersionResponse>(
    `${path(automationId)}/versions/${versionId}`
  )
  return data
}

/** A version of the automation, by id. */
function versionPath(automationId: string, versionId: string): string {
  return `${path(automationId)}/versions/${encodeURIComponent(versionId)}`
}

/**
 * Save: write the active draft (`draftId`), or with none start one holding
 * this definition, in any state; it comes back with what's left to do on it
 * (`issues`). Nothing live changes. `expected` is the draft's `updated_at` the
 * editor last saw: someone else's save since, or another draft made active,
 * is refused (409 `automation_draft_changed`). Left out, nothing is checked.
 */
export async function saveAutomationDraft(
  automationId: string,
  definition: AutomationDefinitionContract,
  draftId: string | null,
  expected?: string
): Promise<AutomationVersionResponse> {
  if (draftId === null) {
    // A new draft overwrites nothing, so there's nothing to check.
    const { data } = await apiClient.post<AutomationVersionResponse>(
      `${path(automationId)}/versions`,
      definition
    )
    return data
  }
  const url = versionPath(automationId, draftId)
  const { data } =
    expected && expected !== 'none'
      ? await apiClient.put<AutomationVersionResponse>(url, definition, { params: { expected } })
      : await apiClient.put<AutomationVersionResponse>(url, definition)
  return data
}

/**
 * The active draft (`draftId`) becomes the live definition; the version it
 * replaces is archived. Validated again, since its table may have changed
 * since it was saved.
 */
export async function publishAutomationDraft(
  automationId: string,
  draftId: string
): Promise<AutomationDetailResponse> {
  const { data } = await apiClient.post<AutomationDetailResponse>(
    `${versionPath(automationId, draftId)}/publish`,
    undefined,
    { timeout: SENDER_CHECK_TIMEOUT_MS }
  )
  return data
}

/**
 * Copy an earlier version into the draft, replacing any draft; with `publish`
 * also publish it. Resolves to the resulting draft, or the published version.
 */
export async function restoreAutomationVersion(
  automationId: string,
  versionId: string,
  payload: AutomationRestoreRequest = {}
): Promise<AutomationVersionResponse> {
  const { data } = await apiClient.post<AutomationVersionResponse>(
    `${path(automationId)}/versions/${versionId}/restore`,
    payload,
    { timeout: SENDER_CHECK_TIMEOUT_MS }
  )
  return data
}

/** A new draft from the published version, made the active one. */
export async function createAutomationDraft(
  automationId: string
): Promise<AutomationVersionResponse> {
  const { data } = await apiClient.post<AutomationVersionResponse>(`${path(automationId)}/versions`)
  return data
}

/** Makes one of the drafts the one the editor works on. */
export async function activateAutomationDraft(
  automationId: string,
  versionId: string
): Promise<AutomationDetailResponse> {
  return updateAutomation(automationId, { active_draft_id: versionId })
}

/**
 * Deletes one draft (discarding the active one); if it was the active one, the
 * newest other draft becomes active. Published and archived versions stay.
 */
export async function deleteAutomationDraft(
  automationId: string,
  versionId: string
): Promise<void> {
  await apiClient.delete(versionPath(automationId, versionId))
}

/** Moves it to the trash: it stops running but keeps its versions and run history. */
export async function deleteAutomation(automationId: string): Promise<AutomationDetailResponse> {
  const { data } = await apiClient.delete<AutomationDetailResponse>(path(automationId))
  return data
}

/** GET /automations?deleted=true: the trash, most recently deleted first. */
export async function listDeletedAutomations(): Promise<AutomationDetailResponse[]> {
  const { data } = await apiClient.get<AutomationDetailResponse[]>('/automations', {
    params: { deleted: true },
  })
  return data
}

/** Brings it back from the trash as it was. */
export async function restoreAutomation(automationId: string): Promise<AutomationDetailResponse> {
  const { data } = await apiClient.post<AutomationDetailResponse>(`${path(automationId)}/restore`)
  return data
}

/** Only from the trash: its versions, runs and emails are deleted with it. */
export async function deleteAutomationPermanently(automationId: string): Promise<void> {
  await apiClient.delete(path(automationId), { params: { permanently: true } })
}

/**
 * Stops a run that hasn't finished: one that hasn't started is cancelled; one
 * that has keeps what it did and does nothing more.
 */
export async function stopAutomationRun(
  automationId: string,
  runId: string
): Promise<AutomationRunResponse> {
  const { data } = await apiClient.post<AutomationRunResponse>(
    `${path(automationId)}/runs/${encodeURIComponent(runId)}/stop`
  )
  return data
}

/** Runs across every automation, newest first, with the total that match. */
export async function listAllAutomationRuns(
  params: AutomationRunListParams = {}
): Promise<AutomationRunPageResponse> {
  const { data } = await apiClient.get<AutomationRunPageResponse>('/automations/runs', { params })
  return data
}

/** Newest first. */
export async function listAutomationRuns(
  automationId: string,
  params: AutomationPageParams = {}
): Promise<AutomationRunResponse[]> {
  const { data } = await apiClient.get<AutomationRunResponse[]>(`${path(automationId)}/runs`, {
    params,
  })
  return data
}

export async function getAutomationRun(
  automationId: string,
  runId: string,
  signal?: AbortSignal
): Promise<AutomationRunResponse> {
  const { data } = await apiClient.get<AutomationRunResponse>(
    `${path(automationId)}/runs/${runId}`,
    { signal }
  )
  return data
}

/** One page of the emails a run sent, in the order it reached them. */
export async function listAutomationRunEmails(
  automationId: string,
  runId: string,
  params: AutomationPageParams = {},
  signal?: AbortSignal
): Promise<AutomationDeliveryResponse[]> {
  const { data } = await apiClient.get<AutomationDeliveryResponse[]>(
    `${path(automationId)}/runs/${runId}/emails`,
    { params, signal }
  )
  return data
}

/**
 * Run now or Test: queued on the server, then sent by the worker within
 * about 10 seconds. 409 `automation_disabled` for Run now of an automation
 * that's off, 409 `automation_run_in_progress` while another Run now waits or
 * sends, 409 `automation_reads_missing_data` when its email reads what's gone.
 */
export async function startAutomationRun(
  automationId: string,
  payload: StartAutomationRunRequest
): Promise<StartAutomationRunResponse> {
  const { data } = await apiClient.post<StartAutomationRunResponse>(
    `${path(automationId)}/runs`,
    payload,
    { timeout: SENDER_CHECK_TIMEOUT_MS }
  )
  return data
}

/** Where a run by hand's email goes in this environment, and how many it sends. */
export async function getAutomationEmailSettings(): Promise<AutomationEmailDeliveryResponse> {
  const { data } = await apiClient.get<AutomationEmailDeliveryResponse>(
    '/automations/email-settings'
  )
  return data
}

/** The calendar schedules run on: the server's holidays, workweek and working hours. */
export async function getAutomationScheduleCalendar(): Promise<AutomationScheduleCalendarResponse> {
  const { data } = await apiClient.get<AutomationScheduleCalendarResponse>(
    '/automations/schedule-calendar'
  )
  return data
}

/** The published version's emails for its first `limit` (1-50) matching records. Sends nothing. */
export async function previewAutomation(
  automationId: string,
  limit: number
): Promise<AutomationPreviewResponse> {
  const { data } = await apiClient.post<AutomationPreviewResponse>(
    `${path(automationId)}/preview`,
    undefined,
    { params: { limit } }
  )
  return data
}

/** Arvoke AI's read of whether an email step reads as promotional, for the editor's warning. */
export async function checkEmailPurpose(
  request: EmailPurposeCheckRequest
): Promise<EmailPurposeCheckResponse> {
  const { data } = await apiClient.post<EmailPurposeCheckResponse>(
    '/automations/email-purpose-check',
    request
  )
  return data
}

/** Like `previewAutomation`, for unsaved edits; validated like a save. Sends nothing. */
export async function previewAutomationDraft(
  draft: AutomationPreviewRequest,
  limit: number
): Promise<AutomationPreviewResponse> {
  const { data } = await apiClient.post<AutomationPreviewResponse>('/automations/preview', draft, {
    params: { limit },
  })
  return data
}

/** Like `previewAutomation`, for one version (the draft, say). Sends nothing. */
export async function previewAutomationVersion(
  automationId: string,
  versionId: string,
  limit: number
): Promise<AutomationPreviewResponse> {
  const { data } = await apiClient.post<AutomationPreviewResponse>(
    `${path(automationId)}/versions/${versionId}/preview`,
    undefined,
    { params: { limit } }
  )
  return data
}

// A 50 MB file on a slow connection takes longer than the API client's 30 s.
const UPLOAD_TIMEOUT_MS = 5 * 60 * 1000

/** What files an email step can attach, and how big. */
export async function getAutomationFileLimits(): Promise<AutomationFileLimitsResponse> {
  const { data } = await apiClient.get<AutomationFileLimitsResponse>('/automations/files/limits')
  return data
}

/** Uploads one file for an email step; it's kept once a draft names it. */
export async function uploadAutomationFile(
  file: File,
  options: { signal?: AbortSignal; onProgress?: (fraction: number) => void } = {}
): Promise<AutomationFileResponse> {
  const form = new FormData()
  form.append('file', file, file.name)
  // postForm, not post: the client's JSON Content-Type would send no file.
  const { data } = await apiClient.postForm<AutomationFileResponse>('/automations/files', form, {
    signal: options.signal,
    timeout: UPLOAD_TIMEOUT_MS,
    onUploadProgress: (event) => {
      if (event.total) options.onProgress?.(event.loaded / event.total)
    },
  })
  return data
}

/** Deletes an upload no draft has saved yet. */
export async function deleteAutomationFile(fileId: string): Promise<void> {
  await apiClient.delete(`/automations/files/${encodeURIComponent(fileId)}`)
}

/** A link that downloads an uploaded file, for five minutes. */
export async function getAutomationFileLink(fileId: string): Promise<AutomationFileLinkResponse> {
  const { data } = await apiClient.get<AutomationFileLinkResponse>(
    `/automations/files/${encodeURIComponent(fileId)}/link`
  )
  return data
}

/** A Content-Disposition header's file name (`filename*=` first), if it has one. */
export function contentDispositionFileName(disposition: string | undefined): string | null {
  if (!disposition) return null
  const encoded = /filename\*=(?:UTF-8)?''([^;]+)/i.exec(disposition)
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1])
    } catch {
      // The plain form follows.
    }
  }
  const plain = /filename=("([^"]+)"|([^;\s]+))/i.exec(disposition)
  return plain ? (plain[2] ?? plain[3] ?? null) : null
}

/** A file from data as one previewed email would carry it. Sends nothing. */
export async function getAutomationPreviewFile(
  request: AutomationPreviewFileRequest
): Promise<{ blob: Blob; fileName: string | null }> {
  const response = await apiClient.post<Blob>('/automations/preview/file', request, {
    responseType: 'blob',
    timeout: UPLOAD_TIMEOUT_MS,
  })
  const disposition = response.headers['content-disposition'] as string | undefined
  return { blob: response.data, fileName: contentDispositionFileName(disposition) }
}
